/**
 * useNPSDEntity — shared hook for NPSD entity screens (Person, Business, Vehicle, Client).
 *
 * Extracts common patterns:
 *  - Connection search/view modal state management
 *  - Address validation with suggestions
 *  - ensureConnectionsNewPopulated logic
 *  - Attachment upload/remove handlers
 */

import { useState, useCallback } from 'react';
import { App } from '@/services/app-compat';
import { validateAddressWithSuggestions } from '@/utils/custom-location';
import { getCurrentCompanyId } from '@/utils/auth';

// Window globals used by NPSD entity screens
const fillInAddressFromSuggestions = (window as any).fillInAddressFromSuggestions;
const DataObjectLocationFields = (window as any).DataObjectLocationFields;

// ── Types ────────────────────────────────────────────────────────────────

type EntityType = 'person' | 'business' | 'vehicle' | 'client';

interface UseNPSDEntityOptions {
  entityType: EntityType;
  setEntity: (updater: (prev: any) => any) => void;
  /** Connection types this entity supports (excluding itself) */
  connectionTypes: string[];
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function useNPSDEntity({ entityType, setEntity, connectionTypes }: UseNPSDEntityOptions) {

  // ── Connection Search Modal State ────────────────────────────────────

  const [showConnectionSearchModal, setShowConnectionSearchModal] = useState(false);
  const [connectionSearchType, setConnectionSearchType] = useState('');

  const openConnectionSearch = useCallback((type: string) => {
    setConnectionSearchType(type);
    setShowConnectionSearchModal(true);
  }, []);

  const closeConnectionSearchModal = useCallback(() => {
    setShowConnectionSearchModal(false);
    setConnectionSearchType('');
  }, []);

  // ── Connection View Modal State ──────────────────────────────────────

  const [showViewPersonModal, setShowViewPersonModal] = useState(false);
  const [viewPersonId, setViewPersonId] = useState<number | null>(null);
  const [showViewVehicleModal, setShowViewVehicleModal] = useState(false);
  const [viewVehicleId, setViewVehicleId] = useState<number | null>(null);
  const [showViewBusinessModal, setShowViewBusinessModal] = useState(false);
  const [viewBusinessId, setViewBusinessId] = useState<number | null>(null);
  const [showViewClientModal, setShowViewClientModal] = useState(false);
  const [viewClientId, setViewClientId] = useState<number | null>(null);

  const openViewPersonModal = useCallback((id: number) => {
    setViewPersonId(id);
    setShowViewPersonModal(true);
  }, []);

  const openViewVehicleModal = useCallback((id: number) => {
    setViewVehicleId(id);
    setShowViewVehicleModal(true);
  }, []);

  const openViewBusinessModal = useCallback((id: number) => {
    setViewBusinessId(id);
    setShowViewBusinessModal(true);
  }, []);

  const openViewClientModal = useCallback((id: number) => {
    setViewClientId(id);
    setShowViewClientModal(true);
  }, []);

  /** Open the appropriate view modal based on connectionSearchType. */
  const handleConnectionView = useCallback((id: number) => {
    switch (connectionSearchType) {
      case 'person': openViewPersonModal(id); break;
      case 'vehicle': openViewVehicleModal(id); break;
      case 'business': openViewBusinessModal(id); break;
      case 'client': openViewClientModal(id); break;
    }
  }, [connectionSearchType, openViewPersonModal, openViewVehicleModal, openViewBusinessModal, openViewClientModal]);

  // ── Address Validation ───────────────────────────────────────────────

  /**
   * Validates and geocodes the address. Returns an object with the filled
   * address fields on success, or null on failure/cancellation.
   * Also updates React state, but callers should merge the returned fields
   * into their save object since state updates are async.
   *
   * Pass `original` (the entity as loaded from the server) to skip geocoding
   * when the address hasn't changed since load.
   */
  const validateAndFillAddress = useCallback((
    formattedAddress: string | undefined | null,
    original?: { locFormattedAddress?: any } | null,
  ): Promise<Record<string, any> | null> => {
    return new Promise((resolve) => {
      const addressValue = formattedAddress?.trim() || '';
      if (!addressValue) {
        const clearedFields = {
          locAddress: '',
          locCity: '',
          locState: '',
          locZip: '',
          locCountry: '',
          latitude: null,
          longitude: null,
          customLocationId: null,
        };
        setEntity((prev: any) => ({ ...prev, ...clearedFields }));
        resolve(clearedFields);
        return;
      }
      if (original && (original.locFormattedAddress || '') === (formattedAddress || '')) {
        resolve({});
        return;
      }
      validateAddressWithSuggestions({
        address: addressValue,
        errorMessage: 'Error validating address. Please re-enter the address.',
        isLocationRequired: false,
        companyId: getCurrentCompanyId(),
        // Pass the previously-saved coords so the backend can detect discrepancies (>500m
        // move) on edits and surface the dual-pin confirmation UX. Null on first save.
        previousLat: (original as any)?.latitude ?? null,
        previousLng: (original as any)?.longitude ?? null,
      }).then((results: any) => {
        const filledFields: Record<string, any> = {};
        // fillInAddressFromSuggestions mutates the target object, so we pass
        // a temp object and capture the fields it sets.
        const tempObj: any = {};
        fillInAddressFromSuggestions(results, tempObj, null, DataObjectLocationFields[entityType]);
        Object.assign(filledFields, tempObj);
        setEntity((prev: any) => ({ ...prev, ...filledFields }));
        resolve(filledFields);
      }).catch(() => {
        resolve(null);
      });
    });
  }, [entityType, setEntity]);

  // ── Ensure Connections New Populated ──────────────────────────────────

  const ensureConnectionsNewPopulated = useCallback((entity: any): any => {
    const updated = JSON.parse(JSON.stringify(entity));
    if (updated.connections) {
      if (!updated.connectionsNew) {
        const empty: Record<string, any[]> = {};
        connectionTypes.forEach(t => { empty[t] = []; });
        updated.connectionsNew = empty;
      }
      connectionTypes.forEach((type) => {
        const existing = updated.connections[type] || [];
        const newConns = updated.connectionsNew[type] || [];
        existing.forEach((conn: any) => {
          const inNew = newConns.some((n: any) => n.id === conn.id);
          if (!inNew) {
            if (!updated.connectionsNew[type]) {
              updated.connectionsNew[type] = [];
            }
            updated.connectionsNew[type].push(conn);
          }
        });
      });
    }
    return updated;
  }, [connectionTypes]);

  // ── Attachment Handlers ──────────────────────────────────────────────

  const handleAttachmentUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    if (!fileInput.files || fileInput.files.length === 0) return;

    if (typeof App !== 'undefined' && App.uploadFromFileInput) {
      App.uploadFromFileInput(fileInput, {
        updateAttachmentInfo: (_idx: number, attachmentInfo: any) => {
          setEntity((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              attachmentInfoList: [...(prev.attachmentInfoList || []), attachmentInfo],
            };
          });
        },
        updateAttachment: (_idx: number, attachment: any) => {
          setEntity((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              attachmentList: [...(prev.attachmentList || []), attachment],
            };
          });
        },
        successCallback: (_idx: number, attachment: any) => {
          setEntity((prev: any) => {
            if (!prev) return prev;
            const updatedList = (prev.attachmentList || []).map((a: any) => {
              if (a.id === attachment.id) {
                return { ...a, signedURL: attachment.signedURL + '#' + new Date().getTime() };
              }
              return a;
            });
            return { ...prev, attachmentList: updatedList };
          });
        },
      });
    }
    fileInput.value = '';
  }, [setEntity]);

  const removeAttachment = useCallback((attachment: any) => {
    setEntity((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev };
      updated.attachmentList = (updated.attachmentList || []).filter((a: any) => a.id !== attachment.id);
      updated.attachmentInfoList = (updated.attachmentInfoList || []).filter((a: any) => a.id !== attachment.id);
      // Clear description pic if it was the removed attachment
      if (updated.descriptionPicId?.toString() === attachment.id?.toString()) {
        updated.descriptionPicId = null;
        updated.descriptionPicInfoId = null;
      }
      return updated;
    });
  }, [setEntity]);

  // ── Return ───────────────────────────────────────────────────────────

  return {
    // Connection search modal
    showConnectionSearchModal,
    connectionSearchType,
    openConnectionSearch,
    closeConnectionSearchModal,

    // Connection view modals
    showViewPersonModal,
    viewPersonId,
    setShowViewPersonModal,
    showViewVehicleModal,
    viewVehicleId,
    setShowViewVehicleModal,
    showViewBusinessModal,
    viewBusinessId,
    setShowViewBusinessModal,
    showViewClientModal,
    viewClientId,
    setShowViewClientModal,
    openViewPersonModal,
    openViewVehicleModal,
    openViewBusinessModal,
    openViewClientModal,
    handleConnectionView,

    // Address validation
    validateAndFillAddress,

    // Connections
    ensureConnectionsNewPopulated,

    // Attachments
    handleAttachmentUpload,
    removeAttachment,
  };
}
